import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl, formatRupiah, formatApiError } from "@/lib/api";
import { Building2 } from "lucide-react";
import { toast } from "sonner";

const COLUMNS = [
  { k: "baru", t: "Baru", color: "border-blue-500/40 text-blue-300" },
  { k: "on_market", t: "On Market", color: "border-emerald-500/40 text-emerald-300" },
  { k: "nego", t: "Negosiasi", color: "border-amber-500/40 text-amber-300" },
  { k: "reserved", t: "Reserved", color: "border-cyan-500/40 text-cyan-300" },
  { k: "closing", t: "Closing", color: "border-purple-500/40 text-purple-300" },
  { k: "sold", t: "SOLD", color: "border-slate-500/40 text-slate-200" },
];

export default function Kanban() {
  const [listings, setListings] = useState([]);
  const [dragId, setDragId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/listings?mine=true");
    setListings(data);
  };
  useEffect(() => { load(); }, []);

  const onDrop = async (status, droppedId) => {
    const id = droppedId || dragId;
    setDragId(null);
    if (!id) return;
    const cur = listings.find((l) => l.id === id);
    if (!cur || cur.status === status) return;
    setListings((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await api.patch(`/listings/${id}/status`, { status });
      toast.success(`Dipindah ke ${status.replace("_", " ")}`);
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  return (
    <div className="space-y-6" data-testid="kanban-page">
      <div>
        <span className="text-xs uppercase tracking-widest text-orange-400 font-semibold">Pipeline</span>
        <h1 className="text-3xl font-extrabold tracking-tight mt-1">Kanban Status Listing</h1>
        <p className="text-slate-400 text-sm mt-1">Tarik &amp; letakkan untuk ubah status.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {COLUMNS.map((col) => {
          const items = listings.filter((l) => l.status === col.k);
          return (
            <div key={col.k}
                 data-testid={`kanban-col-${col.k}`}
                 onDragOver={(e) => e.preventDefault()}
                 onDrop={(e) => { e.preventDefault(); onDrop(col.k, e.dataTransfer.getData("text/plain")); }}
                 className={`kanban-col rounded-xl bg-[#0D1322] border ${col.color} p-3 space-y-3`}>
              <div className="flex items-center justify-between px-1">
                <div className={`text-[11px] uppercase tracking-widest font-bold ${col.color.split(" ").slice(-1)[0]}`}>{col.t}</div>
                <div className="text-xs text-slate-500">{items.length}</div>
              </div>
              {items.length === 0 && (
                <div className="text-xs text-slate-600 text-center py-6 border border-dashed border-white/5 rounded-lg">Drop di sini</div>
              )}
              {items.map((l) => (
                <div key={l.id}
                     draggable
                     onDragStart={(e) => { e.dataTransfer.setData("text/plain", l.id); e.dataTransfer.effectAllowed = "move"; setDragId(l.id); }}
                     data-testid={`kanban-card-${l.id}`}
                     className="rounded-lg bg-[#141C2E] border border-white/5 overflow-hidden cursor-grab hover:border-orange-500/40 transition">
                  <Link to={`/app/listings/${l.id}`}>
                    <div className="aspect-video bg-slate-800">
                      {l.photos?.[0] ? (
                        <img src={fileUrl(l.photos[0])} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full grid place-items-center text-slate-600"><Building2 className="w-8 h-8" /></div>
                      )}
                    </div>
                    <div className="p-3">
                      <div className="text-sm font-semibold text-slate-100 line-clamp-1">{l.title}</div>
                      <div className="text-xs text-slate-400 line-clamp-1">{l.location}</div>
                      <div className="text-sm text-orange-400 font-bold mt-1">{formatRupiah(l.price)}</div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
